import { API_BASE_URL, getBaseHeaders } from './api.config';
import { throwApiError } from './api.error';
import { authenticatedCall } from './auth';
import type {
  ReportBreakdown,
  ReportGranularity,
  ReportDateField,
  SellerOrder,
  SellerOrderLocationOption,
  SellerOrderLocation,
  SellerOrderLocationRequest,
  SellerOrderOverview,
  SellerOrderProductOption,
  SellerOrderReport,
  SellerOrderRequest,
  SellerOrderStatus,
} from './api.types';

export interface SellerOrderFilters {
  from: string;
  to: string;
  customerName?: string;
  statuses?: SellerOrderStatus[];
  locationId?: string;
  orderLocationId?: string;
  productId?: string;
}

export interface SellerOrderReportFilters {
  from: string;
  to: string;
  granularity: ReportGranularity;
  dateField: ReportDateField;
  breakdown: ReportBreakdown;
  statuses?: SellerOrderStatus[];
  locationId?: string;
  orderLocationId?: string;
  productId?: string;
}

const queryString = (values: object) => {
  const params = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (typeof value === 'string' && value) params.set(key, value);
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (typeof item === 'string' && item) params.append(key, item);
      });
    }
  });
  return params.toString();
};

const jsonRequest = async <T>(url: string, options: RequestInit = {}): Promise<T> => {
  const response = await authenticatedCall(url, {
    ...options,
    headers: {
      ...getBaseHeaders(),
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  if (!response.ok) await throwApiError(response);
  return response.json();
};

export const fetchSellerOrderProductOptions = (): Promise<SellerOrderProductOption[]> =>
  jsonRequest(`${API_BASE_URL}/seller-orders/product-options`);

export const fetchSellerOrderLocationOptions = (): Promise<SellerOrderLocationOption[]> =>
  jsonRequest(`${API_BASE_URL}/seller-orders/location-options`);

export const fetchSellerOrderLocations = (): Promise<SellerOrderLocation[]> =>
  jsonRequest(`${API_BASE_URL}/seller-order-locations`);

export const createSellerOrderLocation = (request: SellerOrderLocationRequest): Promise<SellerOrderLocation> =>
  jsonRequest(`${API_BASE_URL}/seller-order-locations`, {
    method: 'POST',
    body: JSON.stringify(request),
  });

export const updateSellerOrderLocation = (
  id: string,
  request: SellerOrderLocationRequest,
): Promise<SellerOrderLocation> => jsonRequest(`${API_BASE_URL}/seller-order-locations/${id}`, {
  method: 'PUT',
  body: JSON.stringify(request),
});

export const deleteSellerOrderLocation = async (id: string): Promise<void> => {
  const response = await authenticatedCall(`${API_BASE_URL}/seller-order-locations/${id}`, {
    method: 'DELETE',
    headers: getBaseHeaders(),
  });
  if (!response.ok) await throwApiError(response);
};

export const restoreSellerOrderLocation = (id: string): Promise<SellerOrderLocation> =>
  jsonRequest(`${API_BASE_URL}/seller-order-locations/${id}/restore`, { method: 'PATCH' });

export const fetchSellerOrders = (filters: SellerOrderFilters): Promise<SellerOrderOverview> =>
  jsonRequest(`${API_BASE_URL}/seller-orders?${queryString(filters)}`);

export const createSellerOrder = (request: SellerOrderRequest): Promise<SellerOrder> =>
  jsonRequest(`${API_BASE_URL}/seller-orders`, {
    method: 'POST',
    body: JSON.stringify(request),
  });

export const updateSellerOrder = (orderId: string, request: SellerOrderRequest): Promise<SellerOrder> =>
  jsonRequest(`${API_BASE_URL}/seller-orders/${orderId}`, {
    method: 'PUT',
    body: JSON.stringify(request),
  });

export const updateSellerOrderStatus = (
  orderId: string,
  status: SellerOrderStatus,
): Promise<SellerOrder> => jsonRequest(`${API_BASE_URL}/seller-orders/${orderId}/status`, {
  method: 'PATCH',
  body: JSON.stringify({ status }),
});

export const deleteSellerOrder = async (orderId: string): Promise<void> => {
  const response = await authenticatedCall(`${API_BASE_URL}/seller-orders/${orderId}`, {
    method: 'DELETE',
    headers: getBaseHeaders(),
  });
  if (!response.ok) await throwApiError(response);
};

export const fetchSellerOrderReport = (filters: SellerOrderReportFilters): Promise<SellerOrderReport> =>
  jsonRequest(`${API_BASE_URL}/seller-orders/report?${queryString(filters)}`);
